import { createContext, useContext, useEffect, useState } from 'react';

import { createDxfViewerSession } from './session/createDxfViewerSession';
import type { DxfViewerSession } from './session/types';

const DxfViewerContext = createContext<DxfViewerSession | null>(null);
DxfViewerContext.displayName = 'DxfViewerContext';

/**
 * Owns the page's DXF viewer session.
 *
 * Consumers read controller lenses through `useReadableStore` (or helpers like
 * `useDxfLoad` / `useSessionModel`), so they re-render only on the slice they
 * asked for — never on pan or zoom.
 *
 * Mount once per viewer page, alongside `WebViewerContextProvider`. Doing so on a
 * page whose file is not a DXF costs nothing: a session holds no DOM and no WebGL
 * until `attachSurface` is called, and reports `load.status: 'idle'` until then.
 */
export const DxfViewerProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [session] = useState(createDxfViewerSession);

  // Cleanup the session when the component unmounts.
  useEffect(() => () => session.dispose(), [session]);

  return (
    <DxfViewerContext.Provider value={session}>
      {children}
    </DxfViewerContext.Provider>
  );
};
DxfViewerProvider.displayName = 'DxfViewerProvider';

export const useDxfViewerSession = (): DxfViewerSession => {
  const session = useContext(DxfViewerContext);
  if (!session) {
    throw new Error(
      'useDxfViewerSession must be used within a DxfViewerProvider',
    );
  }
  return session;
};
